/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useEffect, useState, useCallback } from 'react'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import req from '@sportninja/common/api/request'

/**
 * @interface AudienceBreakdownData
 * @property {number} total_users - Total number of users in the audience
 * @property {number} following_users - Number of users following the entity
 * @property {number} player_users - Number of player users
 * @property {number} official_users - Number of official users
 * @property {number} team_official_users - Number of team official users
 * @property {number} league_admin_users - Number of league admin users
 * @property {number} videographer_users - Number of videographer users
 * @property {number} admin_users - Number of admin users
 */
interface AudienceBreakdownData {
  total_users: number
  following_users: number
  player_users: number
  official_users: number
  team_official_users: number
  league_admin_users: number
  videographer_users: number
  admin_users: number
}

/**
 * @interface AudienceBreakdownSectionProps
 * @property {string | undefined} selectedEntityId - ID of the selected entity
 * @property {string | null} selectedEntityType - Type of the selected entity ('org', 'team', 'sched')
 * @property {boolean} activeCompetition - Whether to target only active competitions
 * @property {(isLoading: boolean) => void} [onLoadingChange] - Optional callback to notify parent of loading state changes
 * @property {(data: AudienceBreakdownData | null) => void} [onDataChange] - Optional callback to notify parent of data changes
 */
interface AudienceBreakdownSectionProps {
  selectedEntityId: string | undefined
  selectedEntityType: string | null
  activeCompetition: boolean
  onLoadingChange?: (isLoading: boolean) => void
  onDataChange?: (data: AudienceBreakdownData | null) => void
}

/**
 * Audience Breakdown Section component - displays detailed audience statistics
 *
 * This component shows detailed audience breakdown statistics when an entity is selected.
 * It fetches audience data from the API and displays it in a clean format.
 *
 * Key features:
 * - Fetches and displays detailed audience breakdown
 * - Handles loading states and API errors gracefully
 * - Updates automatically when entity selection changes
 * - Only renders when an entity is selected
 *
 * @param props - Component props as defined in AudienceBreakdownSectionProps
 * @returns JSX element representing the audience breakdown section
 */
const AudienceBreakdownSection: React.FC<AudienceBreakdownSectionProps> =
  React.memo(
    ({
      selectedEntityId,
      selectedEntityType,
      activeCompetition,
      onLoadingChange,
      onDataChange,
    }) => {
      const [audienceData, setAudienceData] =
        useState<AudienceBreakdownData | null>(null)
      const [isLoading, setIsLoading] = useState(false)

      const fetchAudienceData = useCallback(async () => {
        if (!selectedEntityId || !selectedEntityType) {
          setAudienceData(null)
          setIsLoading(false)
          onLoadingChange?.(false)
          onDataChange?.(null)
          return
        }

        try {
          setIsLoading(true)
          onLoadingChange?.(true)
          let type = ''
          if (selectedEntityType?.includes('org')) {
            type = 'organization'
          } else if (selectedEntityType?.includes('team')) {
            type = 'team'
          } else if (
            selectedEntityType?.includes('sched') ||
            selectedEntityType?.includes('competition')
          ) {
            type = 'competition'
          }

          const url = `/social-feed/${selectedEntityId}/${type}${
            activeCompetition
              ? '?active_competition=1'
              : '?active_competition=0'
          }`
          const { data } = await req(url)
          setAudienceData(data)
          onDataChange?.(data)
        } catch (error) {
          console.error('Failed to fetch audience data:', error)
          setAudienceData(null)
          onDataChange?.(null)
        } finally {
          setIsLoading(false)
          onLoadingChange?.(false)
        }
      }, [
        selectedEntityId,
        selectedEntityType,
        activeCompetition,
        onLoadingChange,
        onDataChange,
      ])

      useEffect(() => {
        fetchAudienceData()
      }, [fetchAudienceData])

      // Always render the container, but show "No audience data available" if no entity is selected
      const hasSelectedEntity = selectedEntityId && selectedEntityType

      return (
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              justify-content: space-between;
              align-items: flex-start;
              display: flex;
            `}
          >
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Audience Breakdown
            </Text>
          </div>

          {!hasSelectedEntity ? (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_100}
            >
              No audience data available
            </Text>
          ) : isLoading ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                margin-top: 4px;
              `}
            >
              <Spin size='small' />
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                Loading...
              </Text>
            </div>
          ) : audienceData ? (
            <>
              {audienceData.admin_users > 0 ? (
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Admins: {(audienceData?.admin_users || 0).toLocaleString()}{' '}
                  User
                  {audienceData?.admin_users > 1 ? 's' : ''}
                </Text>
              ) : null}
              {audienceData.league_admin_users > 0 ? (
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  League Admins:{' '}
                  {(audienceData?.league_admin_users || 0).toLocaleString()}{' '}
                  User
                  {audienceData?.league_admin_users > 1 ? 's' : ''}
                </Text>
              ) : null}
              {audienceData.official_users > 0 ? (
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Officials: {audienceData.official_users.toLocaleString()} User
                  {audienceData.official_users > 1 ? 's' : ''}
                </Text>
              ) : null}
              {audienceData.videographer_users > 0 ? (
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Videographers:{' '}
                  {(audienceData?.videographer_users || 0).toLocaleString()}{' '}
                  User
                  {audienceData?.videographer_users > 1 ? 's' : ''}
                </Text>
              ) : null}
              {audienceData.following_users > 0 ? (
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Following: {audienceData.following_users.toLocaleString()}{' '}
                  User
                  {audienceData.following_users > 1 ? 's' : ''}
                </Text>
              ) : null}
              {audienceData.player_users > 0 ? (
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Players: {(audienceData?.player_users || 0).toLocaleString()}{' '}
                  User
                  {audienceData?.player_users > 1 ? 's' : ''}
                </Text>
              ) : null}
              {audienceData.team_official_users > 0 ? (
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Team Officials:{' '}
                  {audienceData.team_official_users.toLocaleString()} User
                  {audienceData.team_official_users > 1 ? 's' : ''}
                </Text>
              ) : null}
              <Text
                variant='paragraphXSmall'
                weight='semiBold'
                color={colors.NEUTRAL_50}
              >
                Total: {audienceData.total_users.toLocaleString()} Unique Users
              </Text>
            </>
          ) : (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_100}
            >
              No audience data available
            </Text>
          )}
        </div>
      )
    }
  )

export default AudienceBreakdownSection
